import {
  parsearFecha,
  parsearMonto,
  limpiarConcepto,
  round2,
  fechaDesdePartes,
  crearFiltroPagina,
  armarDetalle,
} from "./helpers";

/**
 * Plantilla de SCOTIABANK (Inverlat).
 *
 * Formato de la sección "Detalle de tus movimientos":
 *   Fecha | Concepto | Origen/Referencia | Depósito | Retiro | Saldo
 *
 * Al extraer el texto del PDF las columnas vacías se colapsan, por lo que NO se
 * puede saber por posición si un importe es Depósito o Retiro. La solución:
 * cada renglón trae su SALDO corriente al final, así que el tipo se deduce por
 * la variación del saldo respecto al movimiento anterior:
 *   - saldo sube  -> Depósito (cargo)
 *   - saldo baja  -> Retiro   (abono)
 *
 * El monto se toma del importe impreso (penúltimo monto en pesos del renglón),
 * que es robusto por línea; el delta de saldo se usa sólo para clasificar el tipo.
 */

// Un renglón de movimiento empieza con "DD MMM" (ej. "06 ENE").
const RE_FECHA_INICIO = /^(\d{1,2})\s+(ENE|FEB|MAR|ABR|MAY|JUN|JUL|AGO|SEP|OCT|NOV|DIC)\b/i;

// Importes en pesos: anclados con "$" para no confundir con porcentajes ("16.00%")
// ni con números de referencia (sin decimales).
const RE_MONTO_PESOS = /\$\s?-?\d{1,3}(?:,\d{3})*\.\d{2}/g;

// Saldo inicial del resumen ("Saldo inicial = $15,822.61" o "Saldo inicial $15,822.61").
const RE_SALDO_INICIAL = /saldo\s+inicial\s*=?\s*(\$?-?\d{1,3}(?:,\d{3})*\.\d{2})/i;

// Renglones que cierran el detalle de un movimiento (totales / resumen del estado).
const RE_FIN_DETALLE = /^\s*(total|saldo|resumen)\b/i;

function montosPesos(linea) {
  return (linea.match(RE_MONTO_PESOS) || [])
    .map(parsearMonto)
    .filter((n) => n != null);
}

const scotiabank = {
  id: "scotiabank",
  nombre: "Scotiabank",
  clavesSat: ["044"],
  // Detección específica: evita falsos positivos cuando "SCOTIABANK" aparece sólo
  // como banco emisor de un SPEI en el estado de cuenta de OTRO banco.
  // Los estados de Scotiabank siempre incluyen "Scotiabank Inverlat", "inverlat"
  // o la sección "Detalle de tus movimientos" (exclusiva de Scotiabank).
  detectar: (texto) => /detalle de tus movimientos|scotiabank\s+inverlat|inverlat/i.test(texto),
  // Encabezado para pre-llenar una conciliación: período + saldo final.
  // Ej: "Periodo 02-ENE-25/31-ENE-25", "Saldo final= $24,962.64".
  extraerEncabezado(lineas) {
    const t = (lineas || []).join("\n");
    const per = t.match(
      /periodo\s+(\d{1,2})-([a-záéíóú]{3,})-(\d{2,4})\s*\/\s*(\d{1,2})-([a-záéíóú]{3,})-(\d{2,4})/i
    );
    const sf = t.match(/saldo\s+final\b[^$\d]*\$?\s*(-?\d{1,3}(?:,\d{3})*\.\d{2})/i);
    return {
      fechaInicial: per ? fechaDesdePartes(per[1], per[2], per[3]) : null,
      fechaFinal: per ? fechaDesdePartes(per[4], per[5], per[6]) : null,
      saldoFinal: sf ? parsearMonto(sf[1]) : null,
    };
  },
  parsear(lineas, anioRef) {
    const movimientos = [];

    // 1) Saldo inicial para arrancar la cadena de saldos.
    let prevSaldo = null;
    for (const l of lineas || []) {
      const m = l.match(RE_SALDO_INICIAL);
      if (m) {
        prevSaldo = parsearMonto(m[1]);
        break;
      }
    }

    // 2) Recorrer en orden; un movimiento empieza con fecha y trae >= 2 importes
    //    en pesos (monto + saldo).
    const arr = lineas || [];
    const esMovimiento = (l) => RE_FECHA_INICIO.test(l) && montosPesos(l).length >= 2;
    const esDePagina = crearFiltroPagina(arr, arr.findIndex(esMovimiento));
    for (let i = 0; i < arr.length; i++) {
      const linea = arr[i];
      const mFecha = linea.match(RE_FECHA_INICIO);
      if (!mFecha) continue;

      const montos = montosPesos(linea);
      if (montos.length < 2) continue;

      // Detalle: líneas siguientes hasta otra fecha. Como esta plantilla no acota la
      // sección, también se corta en un renglón con importe en pesos o de totales.
      const extras = [];
      for (let j = i + 1; j < arr.length; j++) {
        const sig = arr[j];
        if (RE_FECHA_INICIO.test(sig) || montosPesos(sig).length || RE_FIN_DETALLE.test(sig)) break;
        if (!esDePagina(sig)) extras.push(sig);
      }

      const fecha = parsearFecha(`${mFecha[1]} ${mFecha[2]}`, anioRef, [
        "DD MMM",
        "DD/MMM",
      ]);
      if (!fecha) continue;

      const saldo = montos[montos.length - 1];
      const monto = Math.abs(montos[montos.length - 2]); // importe impreso del movimiento

      // Tipo por variación del saldo (depósito = cargo, retiro = abono).
      // Si aún no hay saldo previo (primer movimiento sin saldo inicial), queda null.
      let tipo = null;
      if (prevSaldo != null) {
        const delta = round2(saldo - prevSaldo);
        tipo = delta >= 0 ? "cargo" : "abono";
      }
      prevSaldo = saldo;

      // Concepto: texto entre la fecha y el primer importe en pesos.
      const idxPrimerMonto = linea.search(RE_MONTO_PESOS);
      const concepto = limpiarConcepto(
        linea.slice(mFecha[0].length, idxPrimerMonto > -1 ? idxPrimerMonto : undefined)
      );

      movimientos.push({
        fecha,
        monto: round2(monto),
        tipo,
        concepto,
        detalle: armarDetalle([concepto, ...extras]),
        saldo,
        raw: linea,
        rawExtra: extras.join(" "),
      });
    }

    return movimientos;
  },
};

export default scotiabank;
